import React from "react";
import { Shell } from "../components/Shell";
import { useAuth } from "../auth/AuthContext";
import { Link } from "../router";
import { Icon, IconName } from "../icons";

const ACCESOS: { to: string; label: string; permiso?: string; desc: string; icon: IconName }[] = [
  { to: "/compras", label: "Compras y Recepción", permiso: "compras:ver", desc: "Proveedores, órdenes de compra y recepción de materia prima.", icon: "cart" },
  { to: "/produccion", label: "Producción", permiso: "produccion:ver", desc: "Lotes, procesos de transformación, resultados y mermas.", icon: "factory" },
  { to: "/costeo", label: "Costeo", permiso: "costeo:ver", desc: "Motor de costeo por lote: materia prima, mano de obra y CIF.", icon: "calculator" },
  { to: "/inventarios-op", label: "Inventarios", permiso: "inventarios:ver", desc: "Existencias de materia prima, producto terminado y kardex.", icon: "box" },
  { to: "/comercial", label: "Comercial", permiso: "comercial:ver", desc: "Clientes, listas de precios, pedidos y facturación.", icon: "briefcase" },
  { to: "/gerencial", label: "Gerencial", permiso: "gerencial:ver", desc: "Presupuestos, KPIs, flujo de caja, riesgos y hallazgos.", icon: "barChart" },
  { to: "/contabilidad", label: "Contabilidad", permiso: "contabilidad:ver", desc: "Plan de cuentas, comprobantes, libros, estados financieros y retenciones por tercero.", icon: "book" },
  { to: "/nomina", label: "Nómina", permiso: "nomina:ver", desc: "Empleados, contratos, liquidación, seguridad social, prestaciones sociales y nómina electrónica.", icon: "users" },
  { to: "/cumplimiento", label: "Cumplimiento DIAN", permiso: "cumplimiento:ver", desc: "Información exógena (1001-1009, 2276) y declaraciones de IVA, Retefuente e ICA en borrador.", icon: "fileCheck" },
  { to: "/pos", label: "POS (mostrador)", permiso: "pos:ver", desc: "Turnos de caja, ventas directas de mostrador y arqueo de efectivo.", icon: "creditCard" },
  { to: "/notas", label: "Notas crédito/débito", permiso: "notas:ver", desc: "Ajustes a facturas y ventas de mostrador ya emitidas — devoluciones, descuentos, intereses.", icon: "fileText" },
  { to: "/tesoreria", label: "Tesorería", permiso: "tesoreria:ver", desc: "Cuentas bancarias, movimientos, cuentas por pagar a proveedores, conciliación bancaria y flujo de caja.", icon: "landmark" },
  { to: "/activos", label: "Activos Fijos", permiso: "activos:ver", desc: "Adquisición, depreciación en línea recta, traslados entre centros de costo y bajas de activos fijos.", icon: "archive" },
  { to: "/reportes", label: "Reportes", desc: "Reportes exportables por módulo, en PDF, Excel o Word.", icon: "printer" },
  { to: "/informes", label: "Informes Ejecutivos", permiso: "informes:ver", desc: "Informe ejecutivo en PDF con cifras reales y resumen por IA.", icon: "lineChart" },
  { to: "/empresa", label: "Configuración de empresa", permiso: "core.empresas:ver", desc: "Datos generales, NIT, régimen tributario y tema visual.", icon: "settings" },
  { to: "/usuarios", label: "Usuarios", permiso: "core.usuarios:ver", desc: "Altas, roles asignados y estado de cuenta.", icon: "user" },
  { to: "/roles", label: "Roles y permisos", permiso: "core.roles:ver", desc: "RBAC — matriz de permisos por módulo.", icon: "shield" },
  { to: "/parametros", label: "Parámetros del sistema", permiso: "core.parametros:ver", desc: "SMLV, rendimientos esperados, márgenes.", icon: "sliders" },
  { to: "/catalogos", label: "Catálogos", permiso: "core.catalogos:ver", desc: "Especies, unidades, bodegas y demás listas maestras.", icon: "tag" },
  { to: "/auditoria", label: "Auditoría (bitácora)", permiso: "core.auditoria:ver", desc: "Trazabilidad inmutable de cambios.", icon: "search" },
];

export function Dashboard() {
  const { sesion, tienePermiso, moduloDisponibleParaRuta } = useAuth();
  const visibles = ACCESOS.filter((a) => (!a.permiso || tienePermiso(a.permiso)) && moduloDisponibleParaRuta(a.to));

  return (
    <Shell titulo="Dashboard" breadcrumb="Inicio">
      <div className="mb-6 flex items-center gap-4">
        <div className="h-12 w-12 rounded-2xl bg-accent-light text-accent-dark flex items-center justify-center shrink-0">
          <Icon name="home" className="h-6 w-6" />
        </div>
        <div>
          <h2 className="font-display text-lg font-bold text-slate-800">
            Bienvenido, {sesion?.usuario.nombre?.split(" ")[0] || "usuario"}
          </h2>
          <p className="text-sm text-textmuted mt-0.5">
            {sesion?.empresa?.nombre} · SIGEPRO ERP — el ERP completo para tu operación.
          </p>
        </div>
      </div>

      {tienePermiso("sigepro_ep:ver") && moduloDisponibleParaRuta("/sigepro-ep") && (
        <Link
          to="/sigepro-ep"
          className="card p-5 mb-6 flex items-center gap-4 hover:shadow-card-hover transition-shadow border-l-4 border-l-amber-600 block"
        >
          <div className="h-11 w-11 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
            <Icon name="building" className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="badge bg-amber-100 text-amber-800 text-[10px]">Paquete separado</span>
              <span className="text-sm font-semibold text-slate-800">SIGEPRO-EP — Entidades Públicas</span>
            </div>
            <p className="text-xs text-textmuted mt-1">
              Catálogo General de Cuentas (CGC) para el sector público, importación masiva de catálogo y hoja de
              ruta de cumplimiento DIAN para entidades públicas — un módulo distinto al núcleo privado, porque el
              manejo tributario y contable del sector público es distinto.
            </p>
          </div>
          <span className="text-amber-700 text-sm font-semibold whitespace-nowrap ml-2 shrink-0">Abrir →</span>
        </Link>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {visibles.map((a) => (
          <Link key={a.to} to={a.to} className="card p-4 block hover:shadow-card-hover hover:border-accent/30 transition-all">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg bg-accent-light text-accent-dark flex items-center justify-center shrink-0">
                <Icon name={a.icon} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-800">{a.label}</div>
                <div className="text-xs text-textmuted mt-1">{a.desc}</div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </Shell>
  );
}
